// import moment from "moment";
import { CAREERS_URL, CONTENT_URL, MAIL_TO_URL } from "routes/constant";
import { User } from "hooks/useUserInfo";

const DEV_GA4_ID = "G-ZQL6ZV0PFP";
const PROD_HOSTNAMES = ["healthinherhue.com", "www.healthinherhue.com"];

export const initGA4 = () => {
  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID;

  if (!measurementId) return;

  const isProdHost = PROD_HOSTNAMES.includes(window.location.hostname);
  const isDevId = measurementId === DEV_GA4_ID;

  if (!isProdHost && !isDevId) return;

  (window as any).gtag?.("config", measurementId, {
    debug_mode: import.meta.env.DEV,
  });
};

const track = (eventName: string, properties: Record<string, any> = {}) => {
  (window as any).gtag?.("event", eventName, properties);
};

export const identifyUser = (userId: number | string) => {
  (window as any).gtag?.("set", { user_id: String(userId) });
};

const ONBOARDING_COMPLETED_EVENT_NAME = "onboarding_completed"; // New event created K.S.
const PROVIDER_CONTACT_CLICKED_EVENT_NAME = "provider_contact_clicked"; // Everything renamed K.S.
const USER_CLICKED_SUBMIT_QUESTION_EVENT_NAME = "carepoint_question_started"; // Event renamed K.S.
const USER_LIKED_PROVIDER_EVENT_NAME = "provider_saved"; // Event renamed K.S.
const USER_REGISTERED_EVENT_NAME = "account_created"; // Event renamed K.S.
const USER_SEARCHED_PROVIDERS_EVENT_NAME = "provider_search_performed"; // Event renamed K.S.
const USER_SHARED_ON_SOCIAL_MEDIA_EVENT_NAME = "carepoint_answer_shared"; // Event renamed K.S.
const USER_SIGNED_IN_EVENT_NAME = "login_completed"; // Event renamed K.S.
const USER_SUBMITTED_QUESTION_EVENT_NAME = "carepoint_question_submitted"; // Event renamed K.S.
const USER_UPDATED_PROFILE_EVENT_NAME = "profile_updated"; // Event renamed K.S.
const USER_STARTED_WATCHING_VIDEO_RESPONSE_EVENT_NAME = "carepoint_video_started"; // Event renamed K.S.
const USER_ENDED_WATCHING_VIDEO_RESPONSE_EVENT_NAME = "carepoint_video_completed"; // Event renamed K.S.
const USER_CLICKED_CARE_SQUADS_CTA_EVENT_NAME = "care_squad_interest_clicked"; // Event renamed K.S.
const USER_VISITED_CARE_POINT_EVENT_NAME = "carepoint_page_viewed"; // Event renamed K.S.
const PROVIDER_NO_RESULTS_RETURNED_EVENT_NAME = "provider_no_results_returned"; // New event - net new K.S.

// K.S. no membership is up but we will keep these on for any edge cases
const USER_BECAME_PREMIUM = "membership_signup_completed"; // Event renamed K.S.
const USER_CANCELLED_PREMIUM = "cancelled_premium_account";

// K.S. PAUSED (not in Hubspot Custom Events PDF)
// Functions below kept as no-ops so existing call sites don't break. Revisit once team approves names
// const INDIVIDUAL_COMPLETED_ONBOARDING_PROCCESS_EVENT_NAME = "member_account_completed";
// const PROVIDER_COMPLETED_ONBOARDING_PROCCESS_EVENT_NAME = "provider_account_completed";
// const USER_CLICKED_CARE_POINT_EVENT_NAME = "user_clicked_care_point";
// const USER_CLICKED_COMMUNITY_EVENT_NAME = "user_clicked_community";
// const USER_CLICKED_DIRECTORY_PROVIDERS_CTA_EVENT_NAME = "user_clicked_directory_providers_cta";
// const USER_CLICKED_SHARE_BUTTON_EVENT_NAME = "user_clicked_share_button";
// const USER_CLICKED_VIDEO_RESPONSE_EVENT_NAME = "user_clicked_video_response";
// const USER_MARKED_AS_FAVORITE_VIDEO_RESPONSE_EVENT_NAME = "user_marked_as_favorite_video_response";
// const USER_REVIEWED_PROVIDER_EVENT_NAME = "user_reviewed_provider";
// const USER_CLICKED_ACCOUNT_DELETE_BUTTON = "user_clicked_account_delete_button";
// const USER_CLICKED_COMMUNITY_CTA_EVENT_NAME = "user_clicked_community_cta";
// const USER_VISITED_NEW_PAGE_AFTER_CARE_POINT_EVENT_NAME = "user_visited_new_page_after_care_point";
// const USER_CLICKED_LOGIN_BUTTON_EVENT_NAME = "user_clicked_login_button";
// const USER_CLICKED_SIGNUP_BUTTON_EVENT_NAME = "user_clicked_signup_button";

// K.S. Removed because GA4 automatic page_view and SPA history tracking covers this
// const USER_VIEWED_PAGE_EVENT_NAME = "user_viewed_page";

const allowedRoutesToTrack = [
  "/",
  "/carepoint",
  "/community",
  "/directory/providers",
  "/membership/individuals",
  "/virtual-care-squads",
  "/membership/providers",
  "/health-equity-pledge",
  "/about-us",
  CAREERS_URL,
  CONTENT_URL,
  MAIL_TO_URL,
];

const addPrefixToAttributes = (prefix, attributes = {}) => {
  const newAttributes = {};

  Object.keys(attributes).forEach((key) => {
    newAttributes[`${prefix}_${key}`] = attributes[key];
  });

  return newAttributes;
};

const extractUserData = (userData) => {
  if (!userData) {
    return { anonymous: true };
  }

  const { id, role, races, pronoun, recurring_interval } = userData;

  return {
    id,
    races: races ? races.map((race) => race.label).join(", ") : null,
    pronoun: pronoun ? pronoun.label : null,
    recurring_interval,
    role
  };
};

export const fireUserClickedCarePointEvent = (userData) => {
  // track(USER_CLICKED_CARE_POINT_EVENT_NAME, {
  //   ...extractUserData(userData),
  // });
};

export const fireUserClickedCommunityEvent = (userData) => {
  // track(USER_CLICKED_COMMUNITY_EVENT_NAME, {
  //   ...extractUserData(userData),
  // });
};

export const fireUserClickedDirectoryProvidersCTAEvent = (
  userData: User | null
) => {
  // track(USER_CLICKED_DIRECTORY_PROVIDERS_CTA_EVENT_NAME, {
  //   ...extractUserData(userData),
  // });
};

export const fireUserClickedRequestAnAppointmentEvent = (
  provider,
  user,
  fromVideoResponse
) => {
  track(PROVIDER_CONTACT_CLICKED_EVENT_NAME, {
    from_video_response: !!fromVideoResponse,
    ...addPrefixToAttributes("provider", extractUserData(provider)),
    ...addPrefixToAttributes("requester", extractUserData(user)),
  });
};

export const fireUserClickedSubmitQuestionEvent = (userData: User | null) => {
  track(USER_CLICKED_SUBMIT_QUESTION_EVENT_NAME, {
    ...extractUserData(userData),
  });
};

export const fireUserClickedVideoResponseEvent = (
  answerId: number,
  userData: User | null
) => {
  // track(USER_CLICKED_VIDEO_RESPONSE_EVENT_NAME, {
  //   answer_id: answerId,
  //   ...extractUserData(userData),
  // });
};

export const fireUserCompletedOnboardingProcessEvent = (userData: User) => {
  track(ONBOARDING_COMPLETED_EVENT_NAME , {
    from_care_point: userData.from_care_point,
    ...extractUserData(userData),
  });
};

export const fireUserLikedAProviderEvent = (provider, user) => {
  track(USER_LIKED_PROVIDER_EVENT_NAME, {
    ...addPrefixToAttributes("provider", extractUserData(provider)),
    ...addPrefixToAttributes("user", extractUserData(user)),
  });
};

export const fireUserRegisteredEvent = (userData) => {
  track(USER_REGISTERED_EVENT_NAME, {
    ...extractUserData(userData),
  });
};

export const triggerUserChangedSubscription = (
  userData: User,
  newSubscriptionStatus: string,
  recurringInterval: string
) => {
  track(
    newSubscriptionStatus === "cancel"
      ? USER_CANCELLED_PREMIUM
      : USER_BECAME_PREMIUM,
    {
      from_care_point: userData.from_care_point,
      ...extractUserData({
        ...userData,
        recurring_interval: recurringInterval,
      }),
    }
  );
};

export const fireUserReviewedProviderEvent = (reviewData) => {
  const review = reviewData?.review || {};

  // track(USER_REVIEWED_PROVIDER_EVENT_NAME, {
  //   provider_id: review.provider_id,
  //   appointment_with_provider: review.appointment_with_provider,
  //   overall_rating: review.overall_rating,
  //   bedside_manner_rating: review.bedside_manner_rating,
  //   cultural_sensitivity_rating: review.cultural_sensitivity_rating,
  //   office_experience_rating: review.office_experience_rating,
  //   ease_of_access_rating: review.ease_of_access_rating,
  //   has_written_review: !!review.written_review,
  // });
};

const buildProviderSearchEventParams = (searchParams, searchOptions) => {
  const eventParams: any = {};
  const {
    attention_place_ids,
    care_proficiency_ids,
    insurance_ids,
    language_ids,
    pronoun_ids,
    query,
    specialty_ids
  } = searchParams;

  const getLabelsFromIds = (stringOfIds: string, records: any[]) => {
    const ids = stringOfIds.split(",").map((id: string) => parseInt(id, 10));

    return ids
      .map((id) => records.find((record: any) => record.id === id))
      .filter(Boolean)
      .map((record) => record.label)
      .join(", ");
  };

  eventParams.has_search_query = !!query;
  eventParams.searched_specialties = specialty_ids
    ? getLabelsFromIds(specialty_ids, searchOptions.specialties)
    : null;
  eventParams.searched_insurances = insurance_ids
    ? getLabelsFromIds(insurance_ids, searchOptions.insurances)
    : null;
  eventParams.searched_languages = language_ids
    ? getLabelsFromIds(language_ids, searchOptions.languages)
    : null;
  eventParams.searched_pronouns = pronoun_ids
    ? getLabelsFromIds(pronoun_ids, searchOptions.pronouns)
    : null;
  eventParams.searched_care_proficiencies = care_proficiency_ids
    ? getLabelsFromIds(care_proficiency_ids, searchOptions.careProficiencies)
    : null;
  eventParams.searched_attention_places = attention_place_ids
    ? getLabelsFromIds(attention_place_ids, searchOptions.careDeliveries)
    : null;

  return eventParams;
};

export const fireUserSearchedProvidersEvent = (
  searchParams,
  searchOptions,
  userData
) => {
  track(USER_SEARCHED_PROVIDERS_EVENT_NAME, {
    ...buildProviderSearchEventParams(searchParams, searchOptions),
    ...addPrefixToAttributes("user", extractUserData(userData)),
  });
};

export const fireProviderNoResultsReturnedEvent = (
  searchParams,
  searchOptions,
  userData
) => {
  track(PROVIDER_NO_RESULTS_RETURNED_EVENT_NAME, {
    ...buildProviderSearchEventParams(searchParams, searchOptions),
    ...addPrefixToAttributes("user", extractUserData(userData)),
  });
};

export const fireUserSignedInEvent = (userData) => {
  track(USER_SIGNED_IN_EVENT_NAME, {
    ...extractUserData(userData),
  });
};

export const fireUserSubmittedQuestionEvent = (userData) => {
  track(USER_SUBMITTED_QUESTION_EVENT_NAME, {
    ...extractUserData(userData),
  });
};

export const fireUserUpdatedProfileEvent = (userData, payload) => {
  const payloadContent = payload?.user || payload || {};

  track(USER_UPDATED_PROFILE_EVENT_NAME, {
    ...extractUserData(userData),
    updated_fields: Object.keys(payloadContent).join(", "),
  });
};

// K.S. Removed; see note above
// export const fireUserViewedPageEvent = (
//   userData: User | null,
//   pathName: string
// ) => {
//   if (allowedRoutesToTrack.includes(pathName)) {
//     track(USER_VIEWED_PAGE_EVENT_NAME, {
//       ...addPrefixToAttributes("user", extractUserData(userData)),
//       path: pathName,
//     });
//   }
// };

export const fireUserDeleteAccountCTAClick = (userData) => {
  // track(USER_CLICKED_ACCOUNT_DELETE_BUTTON, {
  //   ...extractUserData(userData),
  // });
};

export const fireUserStartedWatchingVideoResponseEvent = (
  answerId: number,
  userData: User | null
) => {
  track(USER_STARTED_WATCHING_VIDEO_RESPONSE_EVENT_NAME, {
    answer_id: answerId,
    ...extractUserData(userData),
  });
};

export const fireUserEndedWatchingVideoResponseEvent = (
  answerId: number,
  userData: User | null
) => {
  track(USER_ENDED_WATCHING_VIDEO_RESPONSE_EVENT_NAME, {
    answer_id: answerId,
    ...extractUserData(userData),
  });
};

export const fireUserMarkedAsFavoriteVideoResponseEvent = (
  answerId: number,
  userData: User | null
) => {
  // track(USER_MARKED_AS_FAVORITE_VIDEO_RESPONSE_EVENT_NAME, {
  //   answer_id: answerId,
  //   ...extractUserData(userData),
  // });
};

export const fireUserClickedShareButtonEvent = (userData: User | null) => {
  // track(USER_CLICKED_SHARE_BUTTON_EVENT_NAME, {
  //   ...extractUserData(userData),
  // });
};

export const fireUserSharedOnSocialMediaEvent = (
  socialMedia: string,
  userData: User | null
) => {
  track(USER_SHARED_ON_SOCIAL_MEDIA_EVENT_NAME, {
    share_destination: socialMedia,
    ...extractUserData(userData),
  });
};

export const fireUserClickedCommunityCTAEvent = () => {
  // track(USER_CLICKED_COMMUNITY_CTA_EVENT_NAME);
};

export const fireUserClickedCareSquadsCTAEvent = () => {
  track(USER_CLICKED_CARE_SQUADS_CTA_EVENT_NAME);
};

export const fireUserVisitedNewPageAfterCarePointEvent = (
  newPath: string,
  order: number,
  userData: User | null
) => {
  // track(USER_VISITED_NEW_PAGE_AFTER_CARE_POINT_EVENT_NAME, {
  //   new_path: newPath,
  //   order,
  //   ...extractUserData(userData),
  // });
};

export const fireUserVisitedCarePointEvent = (userData: User) => {
  track(USER_VISITED_CARE_POINT_EVENT_NAME, {
    id: userData.id,
    role: userData.role,
    languages: userData.languages
      ? userData.languages.map((language) => language.value).join(", ")
      : null,
  });
};

export const fireUserClickedLoginButtonEvent = (fromCarepoint: boolean) => {
  // track(USER_CLICKED_LOGIN_BUTTON_EVENT_NAME, {
  //   from_care_point: fromCarepoint,
  // });
};

export const fireUserClickedSignUpButtonEvent = (fromCarepoint: boolean) => {
  // track(USER_CLICKED_SIGNUP_BUTTON_EVENT_NAME, {
  //   from_care_point: fromCarepoint,
  // });
};

const Analytics = () => ({
  fireUserClickedCarePointEvent,
  fireUserClickedCommunityEvent,
  fireUserClickedRequestAnAppointmentEvent,
  fireUserClickedSubmitQuestionEvent,
  fireUserClickedDirectoryProvidersCTAEvent,
  fireUserClickedVideoResponseEvent,
  fireUserCompletedOnboardingProcessEvent,
  fireUserLikedAProviderEvent,
  fireUserRegisteredEvent,
  fireUserReviewedProviderEvent,
  fireUserSearchedProvidersEvent,
  fireProviderNoResultsReturnedEvent,
  fireUserSignedInEvent,
  fireUserSubmittedQuestionEvent,
  fireUserUpdatedProfileEvent,
  triggerUserChangedSubscription,
  fireUserStartedWatchingVideoResponseEvent,
  fireUserEndedWatchingVideoResponseEvent,
  fireUserMarkedAsFavoriteVideoResponseEvent,
  fireUserClickedShareButtonEvent,
  fireUserSharedOnSocialMediaEvent,
  fireUserClickedCommunityCTAEvent,
  fireUserClickedCareSquadsCTAEvent,
  fireUserVisitedNewPageAfterCarePointEvent,
  fireUserVisitedCarePointEvent,
  fireUserClickedLoginButtonEvent,
  fireUserClickedSignUpButtonEvent,
});

export default Analytics;
